import { useEffect, useState } from "react";
import { fetchGraspServerInformation, type Nip11Document } from "@/lib/grasp";

export type GraspServerInfoState =
  | { status: "loading" }
  | { status: "found"; document: Nip11Document }
  | { status: "error"; message: string };

const serverInfoCache = new Map<string, Nip11Document>();

function loadServerInfo(domain: string, signal: AbortSignal) {
  const cached = serverInfoCache.get(domain);
  if (cached) return Promise.resolve(cached);
  return fetchGraspServerInformation(domain, signal).then((document) => {
    serverInfoCache.set(domain, document);
    return document;
  });
}

/** Fetch a domain's NIP-11 document with cancellation and a bounded timeout. */
export function useGraspServerInfo(
  domain: string | undefined,
): GraspServerInfoState | undefined {
  const [loaded, setLoaded] = useState<
    { domain: string; state: GraspServerInfoState } | undefined
  >(() => {
    if (!domain) return undefined;
    const cached = serverInfoCache.get(domain);
    return {
      domain,
      state: cached
        ? { status: "found", document: cached }
        : { status: "loading" },
    };
  });

  useEffect(() => {
    if (!domain) {
      return;
    }

    const cached = serverInfoCache.get(domain);
    if (cached) {
      setLoaded({ domain, state: { status: "found", document: cached } });
      return;
    }

    const controller = new AbortController();
    let disposed = false;
    const timeout = window.setTimeout(() => controller.abort(), 8000);
    setLoaded({ domain, state: { status: "loading" } });

    void loadServerInfo(domain, controller.signal)
      .then((document) => {
        if (!disposed) {
          setLoaded({
            domain,
            state: { status: "found", document },
          });
        }
      })
      .catch((error: unknown) => {
        if (disposed) return;
        if (controller.signal.aborted) {
          setLoaded({
            domain,
            state: { status: "error", message: "Server did not respond" },
          });
          return;
        }
        setLoaded({
          domain,
          state: {
            status: "error",
            message: error instanceof Error ? error.message : "Request failed",
          },
        });
      })
      .finally(() => window.clearTimeout(timeout));

    return () => {
      disposed = true;
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [domain]);

  if (!domain) return undefined;
  if (loaded?.domain === domain) return loaded.state;
  const cached = serverInfoCache.get(domain);
  return cached ? { status: "found", document: cached } : { status: "loading" };
}
